import React, { useContext, useEffect, useState } from "react";

import { Error } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Grid,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useParams } from "react-router-dom";

import { SortDirection } from "@safetyculture/s12-apis-connect-web/s12/common/sorting_pb";
import { User } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";
import {
  UserSortType,
  UserStatus,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_user_pb";

import useDebouncedEffect from "../../../../helpers/hooks/use-debounced-effect";
import { GetUsersListParams, getOrgUserList } from "api/endpoints/organisation";
import { getUserOrgs } from "api/endpoints/user";
import { callOptionsHeaders } from "api/helpers";
import { SnackbarContext } from "context/snackbar-context";
import { toUUID } from "helpers/s12-helpers";
import { UserUrlParamTypes } from "types/user-display";

interface RestoreTemplateVersionProps {
  handleTargetUserId: (targetUserId: string) => void;
  handleRestoreTemplateBtn: (isDisabled: boolean) => void;
}

const RestoreTemplateVersion = ({
  handleTargetUserId,
  handleRestoreTemplateBtn,
}: RestoreTemplateVersionProps) => {
  const theme = useTheme();
  const { regionCode, userId } = useParams<UserUrlParamTypes>();
  const [orgUsersList, setOrgUsersList] = useState<User[]>([]);
  const [assigneeId, setAssigneeId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [assigneeInput, setAssigneeInput] = useState("");
  const [assigneeValid, setAssigneeValid] = useState(false);

  const { showError } = useContext(SnackbarContext);

  const getUserList = async (query: string) => {
    setIsLoading(true);

    let orgId = "";
    await getUserOrgs(
      {
        userId: toUUID(userId),
      },
      callOptionsHeaders(regionCode, userId)
    )
      .then(async (res) => {
        orgId = res.organisations[0].id;
      })
      .catch(showError);

    const payload: GetUsersListParams = {
      organizationId: orgId,
      pageSize: 100,
      offset: 0,
      sortDirection: SortDirection.ASC,
      sortType: UserSortType.FIRST_NAME,
      query: query,
      adminOnly: true,
      userStatus: UserStatus.UNSPECIFIED,
    };

    getOrgUserList(payload, callOptionsHeaders(regionCode, orgId))
      .then((res) => {
        setOrgUsersList(res.users);
      })
      .catch(showError)
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSetAssigneeId = (value: string | User | null) => {
    if (value && typeof value === "object") {
      return setAssigneeId(value.id);
    }
  };

  const renderAssigneeInput = () => {
    return (
      <Box>
        <Autocomplete
          data-testid="autocomplete"
          autoHighlight
          autoComplete
          options={orgUsersList}
          loading={isLoading}
          onChange={(event, value, reason) => {
            if (reason === "clear") {
              setAssigneeValid(false);
            }
            if (reason === "selectOption") {
              setAssigneeValid(true);
              handleSetAssigneeId(value);
            }
          }}
          onInputChange={(event, value) => {
            setAssigneeInput(value);
          }}
          getOptionLabel={(option: User) =>
            `${option.firstName} ${option.lastName} - ${option.email}`
          }
          renderOption={(props, option) => (
            <li {...props}>
              <Box>
                <Typography>
                  {option.firstName} {option.lastName}{" "}
                </Typography>
                <Typography variant="caption">{option.email}</Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label="Restore template version from an inspection as new template to user"
              name="assignee"
              variant="outlined"
              margin="normal"
              inputProps={{
                ...params.inputProps,
                "data-testid": "autocomplete-assignee-field",
              }}
            />
          )}
        />
      </Box>
    );
  };

  useEffect(() => {
    handleTargetUserId(assigneeId);
    handleRestoreTemplateBtn(true);
    // handleAssigneeId function not memoized in parent, so may have unintended side effects to include in deps
    // eslint-disable-next-line
  }, [assigneeId]);

  useEffect(() => {
    handleRestoreTemplateBtn(!assigneeValid);
    // eslint-disable-next-line
  }, [assigneeValid]);

  useDebouncedEffect(
    () => {
      getUserList(assigneeInput);
    },
    500,
    [assigneeInput, setAssigneeInput]
  );

  return (
    <>
      <Grid container direction="row" spacing={1} style={{ minWidth: 350 }}>
        <Grid item xs={12} container direction="column">
          <Box
            bgcolor={theme.palette.warning.main}
            borderRadius="5px"
            border={1}
            borderColor={theme.palette.secondary.main}
            marginBottom={1}
          >
            <Grid container alignItems="center">
              <Grid item xs={2} container justifyContent="center">
                <Error fontSize="large" />
              </Grid>
              <Grid item xs={10} style={{ paddingRight: "20px" }}>
                <p>
                  You are about to restore the template which was used to create
                  this inspection.
                </p>
                <p>
                  The template will be restored as a new template with the name
                  format of &ldquo;template name - Restored Version&ldquo;.
                </p>
                <p>
                  The new template will be assigned to the admin user you
                  select:
                </p>
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid item xs={12}>
          {renderAssigneeInput()}
        </Grid>
      </Grid>
    </>
  );
};

export default RestoreTemplateVersion;
